import LazoAuthService from "@/services/LazoAuthService";

/**
 * Feature flags for call sites React Query cannot reach (LAZ-2464).
 *
 * The auth seams that decide where a signed-out person goes run in App's bootstrap effects, in an
 * `AuthEvents.LOGOUT` subscriber, and inside the Axios 401 interceptor. None of those sit under
 * `QueryClientProvider` — the interceptor is not even a component — so the hook in
 * `useAuthQueries` is unavailable there. This module is the imperative read path for the same
 * endpoint; render-time consumers should keep using the hook.
 *
 * Fail-closed by construction: the cached answer starts `false` and only an explicit positive from
 * the backend changes it, so every "we don't know yet" path keeps the legacy login.
 */

let cachedNewLogin = false;
let inFlight: Promise<void> | null = null;
let loaded = false;

/**
 * Fetches the flags once per page load and caches the result. Concurrent callers share the same
 * request — several seams can race on a cold boot, and each firing its own call would put avoidable
 * load in front of sign-in.
 *
 * Never rejects: a failure leaves the cache at its fail-closed default.
 */
export function loadFeatureFlags(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (inFlight) return inFlight;

  inFlight = LazoAuthService.getFeatureFlags()
    .then((response) => {
      const flags = response.getContentOrThrowError();
      cachedNewLogin = flags?.newLogin === true;
      loaded = true;
    })
    .catch(() => {
      // Unreachable or erroring backend: stay closed, and let a later call retry.
      cachedNewLogin = false;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Whether signed-out visitors should be sent to the standalone auth app.
 *
 * Synchronous, so it is safe inside an interceptor or event handler. Returns `false` until a
 * positive answer has actually arrived — callers that can afford to wait should `await
 * loadFeatureFlags()` first, so they act on a real answer rather than the interim default.
 */
export function isNewLoginEnabled(): boolean {
  return cachedNewLogin;
}

/** True once an answer has been received, letting a caller tell "off" from "not known yet". */
export function areFeatureFlagsLoaded(): boolean {
  return loaded;
}

/** Test seam — resets the module cache between cases. */
export function resetFeatureFlagsForTesting(): void {
  cachedNewLogin = false;
  inFlight = null;
  loaded = false;
}
